// ============================================================
// store/simResultStore.js
// EV Powertrain Simulator — 시뮬레이션 결과 전역 상태
// 패턴: 팩토리(Factory) — createSimResultStore() 호출로 인스턴스 생성
// 사용법: import { createSimResultStore } from '../store/simResultStore.js';
// Phase 3: Zustand 상태 관리 도입
// ============================================================

/**
 * 시뮬레이션 결과 스토어 인스턴스를 생성한다.
 *
 * 시뮬레이션 실행 흐름에 따라 상태가 다음 순서로 전이된다:
 *   resetForRun() → [core 계산] → commitCore() → [heavy 계산] → commitHeavy()
 *   오류 발생 시: resetForRun() → commitError()
 *
 * 포함 상태:
 *   simResults   — runHeavySim() 결과 (단일 차량)
 *   compResults  — runComparison() 결과 (A/B 비교)
 *   cycleSim     — Full-Spec CSV 사이클 결과
 *   isRunning    — 계산 진행 중 플래그
 *   simError     — 에러 메시지
 *
 * 영속화: localStorage 저장 없음. 결과는 세션 메모리에만 보관되며
 *          페이지 새로고침 시 재계산이 필요하다.
 *
 * @returns {object}  스토어 인스턴스 (getState, subscribe, 액션 메서드 포함)
 */
function createSimResultStore() {
  let state = {
    simResults:        null,
    compResults:       null,
    cycleSim:          null,
    isRunning:         false,
    simError:          null,
    activeSimVehicle:  'A',   // 현재 단일 탭에서 표시 중인 차량

    // ── 액션 ──────────────────────────────────────

    /**
     * 단일 차량 시뮬레이션 결과를 갱신한다.
     * @param {object|null} r  runHeavySim() 반환값
     */
    setSimResults(r) {
      state.simResults = r;
      state._notify();
    },

    /**
     * A/B 비교 시뮬레이션 결과를 갱신한다.
     * @param {object|null} r  runComparison() 반환값
     */
    setCompResults(r) {
      state.compResults = r;
      state._notify();
    },

    /**
     * CSV 기반 Full-Spec 사이클 시뮬레이션 결과를 갱신한다.
     * @param {object|null} r  사이클 시뮬 결과 객체
     */
    setCycleSim(r) {
      state.cycleSim = r;
      state._notify();
    },

    /**
     * 시뮬레이션 실행 중 여부 플래그를 설정한다.
     * @param {boolean} v  true: 실행 중, false: 완료/유휴
     */
    setIsRunning(v) {
      state.isRunning = v;
      state._notify();
    },

    /**
     * 시뮬레이션 오류 메시지를 설정한다.
     * @param {string|null} msg  오류 메시지 (없으면 null)
     */
    setSimError(msg) {
      state.simError = msg;
      state._notify();
    },

    /**
     * 단일 탭에서 현재 표시 중인 차량을 전환한다.
     * @param {'A'|'B'} v  표시할 차량 식별자
     */
    setActiveSimVehicle(v) {
      state.activeSimVehicle = v;
      state._notify();
    },

    /**
     * 시뮬레이션 실행 직전 상태를 초기화한다.
     *
     * 이전 오류 메시지를 지우고 isRunning을 true로 설정한다.
     * UI에서 로딩 인디케이터를 즉시 표시하기 위해 호출한다.
     */
    resetForRun() {
      state.simError  = null;
      state.isRunning = true;
      state._notify();
    },

    /**
     * Core 단계 시뮬레이션 결과를 커밋한다.
     *
     * Core 계산(빠른 단계)이 완료되면 결과를 저장하고 구독자에게 알린다.
     * isRunning은 여전히 true 상태를 유지하며 Heavy 단계가 이어진다.
     *
     * @param {object|null} simResults   단일 차량 결과
     * @param {object|null} compResults  비교 결과
     */
    commitCore(simResults, compResults) {
      state.simResults  = simResults;
      state.compResults = compResults;
      state._notify();
    },

    /**
     * Heavy 단계 시뮬레이션 결과를 커밋하고 실행 완료 상태로 전환한다.
     *
     * 무거운 계산(Steinmetz 손실, 열 모델 등)이 끝난 뒤 최종 결과를 저장하고
     * isRunning을 false로 설정해 UI 로딩 인디케이터를 해제한다.
     *
     * @param {object|null} simResults   최종 단일 차량 결과
     * @param {object|null} compResults  최종 비교 결과
     */
    commitHeavy(simResults, compResults) {
      state.simResults  = simResults;
      state.compResults = compResults;
      state.isRunning   = false;
      state._notify();
    },

    /**
     * 시뮬레이션 오류를 기록하고 실행 상태를 해제한다.
     *
     * try-catch 블록에서 오류 발생 시 호출하며 isRunning을 false로 전환해
     * UI가 무한 로딩 상태에 빠지지 않도록 한다.
     *
     * @param {string} msg  사용자에게 표시할 오류 메시지
     */
    commitError(msg) {
      state.simError  = msg;
      state.isRunning = false;
      state._notify();
    },

    // ── 내부 ──────────────────────────────────────
    _listeners: new Set(),

    /** 등록된 모든 구독자에게 현재 상태를 동기 전달한다. */
    _notify() { state._listeners.forEach(fn => fn(state)); },

    /**
     * 상태 변경 구독을 등록하고, 해제 함수를 반환한다.
     * @param {(state: object) => void} fn  상태 변경 콜백
     * @returns {() => void}                구독 해제 함수
     */
    subscribe(fn) {
      state._listeners.add(fn);
      return () => state._listeners.delete(fn);
    },

    /**
     * 현재 스토어 상태 스냅샷을 반환한다.
     * @returns {object}
     */
    getState() { return state; },
  };

  return state;
}

export { createSimResultStore };
